/**
 * Mehrere Kassen auf einmal abschliessen.
 *
 * Für den Alltagsfall, dass eine Veranstaltung auf Rechnung läuft: an jeder
 * beteiligten Kasse steht Umsatz, aber kein Kassenschnitt. Am 25.08. waren das
 * zwölf Kassen — sie einzeln aufzuklappen und zwölfmal dieselbe Entscheidung zu
 * treffen ist keine Prüfung, sondern Tipparbeit.
 *
 * Wer Beträge verteilt, sieht die Aufteilung vorher. Gerechnet wird sie in der
 * API, nicht hier.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type CashSessionDto } from "../lib/api";
import { euro } from "../lib/format";
import { Amount, ErrorBox } from "./ui";

type Aktion = "confirm" | "noTip" | "tipTotal";

interface VorschauZeile {
  sessionId: string;
  workstationName: string;
  zBon: string | null;
  cardTotal: string | null;
  tipVorher: string | null;
  tipNachher: string;
}

const TITEL: Record<Aktion, string> = {
  confirm: "So bestätigen",
  noTip: "Ohne Trinkgeld",
  tipTotal: "Trinkgeld gesamt",
};

const ERKLAERUNG: Record<Aktion, string> = {
  confirm: "Übernimmt die Werte, wie sie eingelesen wurden. Ändert nichts an den Beträgen.",
  noTip:
    "Der Umsatz steht, Trinkgeld gab es keines. Der fehlende Gegenwert wird gleich dem vorhandenen gesetzt.",
  tipTotal:
    "Ein Trinkgeld für die ganze Veranstaltung, verteilt im Verhältnis der Umsätze. Wer mehr verkauft hat, bekommt mehr.",
};

export function BulkActions({
  sessions,
  onDone,
  onCancel,
}: {
  sessions: CashSessionDto[];
  onDone: () => void;
  onCancel: () => void;
}) {
  // Keine Vorauswahl. „Ohne Trinkgeld / auf Rechnung" vorzugeben heisst, eine
  // Annahme als geprüfte Tatsache ins Audit-Log zu schreiben, sobald jemand
  // schnell auf Übernehmen klickt.
  const [aktion, setAktion] = useState<Aktion | null>(null);
  const [betrag, setBetrag] = useState("");
  const [grund, setGrund] = useState("");
  const [vorschau, setVorschau] = useState<VorschauZeile[] | null>(null);
  const queryClient = useQueryClient();

  const ids = sessions.map((s) => s.id);
  const payload = (dryRun: boolean) => ({
    sessionIds: ids,
    aktion,
    ...(aktion === "tipTotal" ? { tipTotal: normalize(betrag) } : {}),
    reason: grund.trim() === "" ? undefined : grund.trim(),
    dryRun,
  });

  const rechnen = useMutation({
    mutationFn: () => api.post<{ vorschau: VorschauZeile[] }>("/cash-sessions/bulk", payload(true)),
    onSuccess: (d) => setVorschau(d.vorschau),
  });

  const anwenden = useMutation({
    mutationFn: () =>
      api.post<{ angewandt: number }>("/cash-sessions/bulk", payload(false)),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onDone();
    },
  });

  // Die Aufteilung neu rechnen, sobald sich Betrag, Aktion oder Auswahl ändert.
  // Eine veraltete Vorschau wäre schlimmer als gar keine.
  useEffect(() => {
    setVorschau(null);
    if (aktion !== "tipTotal") return;
    if (normalize(betrag) === "" || Number.isNaN(Number(normalize(betrag)))) return;
    const t = setTimeout(() => rechnen.mutate(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktion, betrag, ids.join(",")]);

  const betragFehlt = aktion === "tipTotal" && normalize(betrag) === "";
  const nichtsGewaehlt = aktion === null;

  return (
    <div className="card mb-4 border-accent/40 p-5">
      <h3 className="mb-1 text-lg">
        {sessions.length === 1 ? "1 Kasse" : `${sessions.length} Kassen`} auf einmal abschliessen
      </h3>
      <p className="mb-4 text-sm text-muted">
        {sessions.map((s) => s.workstationName).join(", ")}
      </p>

      <div className="mb-3 flex flex-wrap gap-2">
        {(["noTip", "tipTotal", "confirm"] as const).map((a) => (
          <button
            key={a}
            className={a === aktion ? "btn-primary" : "btn-outline"}
            onClick={() => setAktion(a)}
          >
            {TITEL[a]}
          </button>
        ))}
      </div>
      <p className="mb-4 max-w-2xl text-xs text-muted">
        {aktion ? ERKLAERUNG[aktion] : "Bitte wählen, was mit diesen Kassen geschehen soll."}
      </p>

      {aktion === "tipTotal" ? (
        <label className="mb-4 block max-w-xs">
          <span className="label mb-1 block">Trinkgeld für alle zusammen</span>
          <input
            className="field num"
            value={betrag}
            onChange={(e) => setBetrag(e.target.value)}
            placeholder="200,00"
            inputMode="decimal"
          />
        </label>
      ) : null}

      {vorschau ? (
        <div className="mb-4 max-w-2xl">
          <div className="label mb-2">So wird verteilt</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted">
                <th className="py-1 text-left font-normal">Kasse</th>
                <th className="py-1 text-right font-normal">Umsatz</th>
                <th className="py-1 text-right font-normal">Trinkgeld</th>
              </tr>
            </thead>
            <tbody>
              {vorschau.map((z) => (
                <tr key={z.sessionId} className="border-t border-line">
                  <td className="py-1">{z.workstationName}</td>
                  <td className="num py-1 text-muted">{euro(z.zBon ?? z.cardTotal)}</td>
                  <td className="num py-1">{euro(z.tipNachher)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-ink/30 font-medium">
                <td className="py-1">Summe</td>
                <td />
                <td className="num py-1">
                  <Amount value={euro(normalize(betrag))} />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}

      <label className="mb-4 block max-w-lg">
        <span className="label mb-1 block">Grund</span>
        <input
          className="field"
          value={grund}
          onChange={(e) => setGrund(e.target.value)}
          placeholder="z. B. Event auf Rechnung"
        />
      </label>

      {rechnen.error ? <ErrorBox error={rechnen.error} /> : null}
      {anwenden.error ? <ErrorBox error={anwenden.error} /> : null}

      <div className="flex flex-wrap gap-2">
        <button
          className="btn-recommend"
          disabled={anwenden.isPending || betragFehlt || nichtsGewaehlt}
          onClick={() => anwenden.mutate()}
        >
          {anwenden.isPending
            ? "Übernimmt"
            : nichtsGewaehlt
              ? "Erst eine Aktion wählen"
              : `Für ${sessions.length} ${sessions.length === 1 ? "Kasse" : "Kassen"} übernehmen`}
        </button>
        <button className="btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

/** Deutsche Eingabe (`1.234,56`) in das API-Format (`1234.56`). */
function normalize(value: string): string {
  const trimmed = value.trim().replace(/\s/g, "");
  if (trimmed.includes(",")) return trimmed.replace(/\./g, "").replace(",", ".");
  return trimmed;
}
